import { el } from './dom.js';
import { carAudio } from '../audio.js';
import { musicPlayer, type MusicSource } from '../music.js';
import { t } from '../i18n/i18n.js';
import './audioSettings.css';

/**
 * The sound settings, as a plain block of controls.
 *
 * It used to carry its own floating button and popover in the corner of every screen; both
 * are gone, and it is now a section inside the Escape menu — see `pauseMenu.ts`. What is
 * left here is only the controls themselves, which is all this ever really was.
 *
 * It owns nothing that the rest of the game needs back. `carAudio` and `musicPlayer` are
 * both page-lifetime singletons already; this is just a face on them.
 */
export class AudioSettings {
  readonly root: HTMLElement;

  private readonly muteBox: HTMLInputElement;
  private readonly sourceButtons: Record<MusicSource, HTMLButtonElement>;
  private readonly hint: HTMLElement;
  private readonly fileInput: HTMLInputElement;
  private readonly urlRow: HTMLElement;
  private readonly urlInput: HTMLInputElement;
  private readonly urlPlayButton: HTMLButtonElement;
  private readonly volumeSlider: HTMLInputElement;
  private readonly status: HTMLElement;

  private pollHandle = 0;
  /** Whether the URL field should be shown, independent of `musicPlayer`'s own source —
   *  the row has to stay open while the player is still typing a URL, before anything has
   *  actually loaded and made `source === 'url'` true. */
  private urlRowOpen = false;

  constructor() {
    this.hint = el('p', { class: 'audio-settings-hint' }, '');
    this.muteBox = el('input', { type: 'checkbox', class: 'audio-settings-mute' });
    this.muteBox.addEventListener('change', () => carAudio.setMuted(this.muteBox.checked));

    const noneButton = el('button', { class: 'audio-settings-source', type: 'button' }, t('Off'));
    const trackButton = el(
      'button',
      { class: 'audio-settings-source', type: 'button', id: 'music-soundtrack' },
      t('Soundtrack'),
    );
    trackButton.addEventListener('click', () => {
      musicPlayer.useTracklist();
      this.sync();
    });
    const fileButton = el('button', { class: 'audio-settings-source', type: 'button' }, t('File'));
    const urlButton = el('button', { class: 'audio-settings-source', type: 'button' }, 'URL');
    this.sourceButtons = { none: noneButton, tracklist: trackButton, file: fileButton, url: urlButton };

    this.fileInput = el('input', {
      type: 'file',
      accept: 'audio/*',
      class: 'audio-settings-file-input',
    });
    this.fileInput.addEventListener('change', () => {
      const file = this.fileInput.files?.[0];
      if (file) musicPlayer.loadFile(file);
      this.sync();
    });

    this.urlInput = el('input', {
      type: 'text',
      class: 'audio-settings-url-input',
      placeholder: 'https://…',
      value: musicPlayer.storedUrl,
    });
    this.urlPlayButton = el(
      'button',
      { class: 'audio-settings-url-play', type: 'button' },
      t('Play'),
    );
    const playFromUrl = () => {
      musicPlayer.loadUrl(this.urlInput.value);
      this.urlRowOpen = true;
      this.sync();
    };
    this.urlPlayButton.addEventListener('click', playFromUrl);
    this.urlInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') playFromUrl();
    });
    this.urlRow = el(
      'div',
      { class: 'audio-settings-url-row is-hidden' },
      this.urlInput,
      this.urlPlayButton,
    );

    noneButton.addEventListener('click', () => {
      musicPlayer.stop();
      this.urlRowOpen = false;
      this.sync();
    });
    fileButton.addEventListener('click', () => this.fileInput.click());
    urlButton.addEventListener('click', () => {
      this.urlRowOpen = true;
      this.sync();
      this.urlInput.focus();
    });

    this.volumeSlider = el('input', {
      type: 'range',
      class: 'audio-settings-volume',
      min: '0',
      max: '100',
      value: String(Math.round(musicPlayer.currentVolume * 100)),
    });
    this.volumeSlider.addEventListener('input', () => {
      musicPlayer.setVolume(Number(this.volumeSlider.value) / 100);
    });

    this.status = el('p', { class: 'audio-settings-status' });

    this.root = el(
      'div',
      { class: 'audio-settings-panel' },
      el(
        'label',
        { class: 'audio-settings-row audio-settings-mute-row' },
        this.muteBox,
        el('span', {}, t('Mute everything')),
        el('kbd', {}, 'M'),
      ),
      el('div', { class: 'audio-settings-divider' }),
      el('div', { class: 'audio-settings-label' }, t('Music')),
      this.hint,
      el(
        'div',
        { class: 'audio-settings-source-row' },
        noneButton,
        trackButton,
        fileButton,
        urlButton,
      ),
      this.fileInput,
      this.urlRow,
      el(
        'div',
        { class: 'audio-settings-row audio-settings-volume-row' },
        el('span', {}, t('Volume')),
        this.volumeSlider,
      ),
      this.status,
    );

    this.sync();
  }

  /**
   * Called when the menu holding this opens or closes.
   *
   * `M` and the music element's own async load/error events can change state this panel
   * does not directly own; a light poll while it is actually on screen is simpler and
   * cheaper than wiring a pub/sub into two singletons that otherwise need none.
   */
  setActive(active: boolean): void {
    window.clearInterval(this.pollHandle);
    this.pollHandle = 0;
    if (!active) return;
    this.sync();
    this.pollHandle = window.setInterval(() => this.sync(), 400);
  }

  private sync(): void {
    this.muteBox.checked = carAudio.muted;
    const music = musicPlayer.debug();
    for (const [source, button] of Object.entries(this.sourceButtons)) {
      button.classList.toggle('is-active', music.source === source);
    }
    // The soundtrack is whatever the server has installed, so both the button and the hint
    // depend on the manifest rather than on anything compiled in.
    this.sourceButtons.tracklist.disabled = music.trackCount === 0;
    this.hint.textContent = music.trackCount
      ? t('The soundtrack plays during races. Or bring your own file, or paste a link.')
      : t('No soundtrack is installed — bring your own file, or paste a link.');
    if (music.source === 'url') this.urlRowOpen = true;
    this.urlRow.classList.toggle('is-hidden', !this.urlRowOpen);
    this.volumeSlider.value = String(Math.round(music.volume * 100));
    if (music.error) {
      this.status.textContent = music.error;
      this.status.classList.add('is-error');
    } else if (music.source === 'none') {
      this.status.textContent = t('No music.');
      this.status.classList.remove('is-error');
    } else if (music.source === 'tracklist' && !music.inRace) {
      this.status.textContent = t('Soundtrack ready: {label} — starts with the race.', { label: music.label ?? '' });
      this.status.classList.remove('is-error');
    } else {
      const label = music.label ?? '';
      this.status.textContent = music.playing ? t('Playing: {label}', { label }) : t('Loading: {label}', { label });
      this.status.classList.remove('is-error');
    }
  }
}
